import * as THREE from "three";

import { elementTint } from "../content/weapon-mods";
import { WEAPONS } from "../content/weapons";
import type { AudioEvent } from "../simulation/snapshot";
import type { CombatJuiceSample } from "../presentation/combat-juice";
import { disposeObject } from "../core/scene-lifecycle";
import { PROJECTILE_COLORS, projectileFamily } from "./projectile-meshes";

type BurstKind = "muzzle" | "impact" | "kill" | "boost";

type Particle = {
  alive: boolean;
  kind: BurstKind;
  life: number;
  maxLife: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  size: number;
  color: number;
  /** Last hex applied to the mesh material — skip setHex when unchanged. */
  appliedColor: number;
};

const MAX_PARTICLES = 96;
const SPEED_LINE_COUNT = 22;
/**
 * Lightweight combat VFX: muzzle flashes, impact sparks, kill pops, boost speed lines.
 * Owned separately from world sync so parallel combat-assist work stays clear.
 * Particle budgets / speed lines follow render quality.
 */
export class CombatVfx {
  private readonly root = new THREE.Group();
  private readonly particles: Particle[] = [];
  private readonly meshes: THREE.Mesh[] = [];
  private readonly speedLines: THREE.Line[] = [];
  private readonly speedLineGeom: THREE.BufferGeometry[] = [];
  private reducedMotion = false;
  private timeS = 0;
  private vfxBudget = MAX_PARTICLES;
  private speedLineBudget = SPEED_LINE_COUNT;

  public constructor(private readonly scene: THREE.Scene) {
    this.root.name = "combat-vfx";
    this.scene.add(this.root);

    const sparkGeo = new THREE.PlaneGeometry(0.55, 0.14);
    for (let i = 0; i < MAX_PARTICLES; i += 1) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffe08a,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      });
      const mesh = new THREE.Mesh(sparkGeo, mat);
      mesh.visible = false;
      this.meshes.push(mesh);
      this.root.add(mesh);
      this.particles.push({
        alive: false,
        kind: "impact",
        life: 0,
        maxLife: 1,
        x: 0,
        y: 0,
        z: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        size: 1,
        color: 0xffe08a,
        appliedColor: -1,
      });
    }

    const lineMat = new THREE.LineBasicMaterial({
      color: 0xe8d4a8,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    for (let i = 0; i < SPEED_LINE_COUNT; i += 1) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
      const line = new THREE.Line(geom, lineMat.clone());
      line.visible = false;
      this.speedLines.push(line);
      this.speedLineGeom.push(geom);
      this.root.add(line);
    }
  }

  public setQuality(budget: number, speedLines: number): void {
    this.vfxBudget = Math.max(8, Math.min(MAX_PARTICLES, budget));
    this.speedLineBudget = Math.max(0, Math.min(SPEED_LINE_COUNT, speedLines));
    for (let i = this.vfxBudget; i < this.particles.length; i += 1) {
      this.particles[i]!.alive = false;
      this.meshes[i]!.visible = false;
    }
    for (let i = this.speedLineBudget; i < this.speedLines.length; i += 1) {
      this.speedLines[i]!.visible = false;
    }
  }

  public setReducedMotion(on: boolean): void {
    this.reducedMotion = on;
    if (on) this.clear();
  }

  public ingest(
    events: readonly AudioEvent[],
    player: { x: number; y: number; altitude: number; facing: number },
  ): void {
    if (this.reducedMotion) return;

    for (const event of events) {
      switch (event.type) {
        case "weapon_fire": {
          const nose = 1.6;
          const x = player.x + Math.cos(player.facing) * nose;
          const z = player.y + Math.sin(player.facing) * nose;
          const style = WEAPONS[event.weaponId]?.projectileStyle ?? "pellet";
          const family = projectileFamily(style);
          const color = PROJECTILE_COLORS[style];
          const count = this.scaleCount(family === "warhead" ? 7 : family === "cone" ? 4 : 5);
          const speed = family === "warhead" ? 3.2 : 5.2;
          this.burst("muzzle", x, player.altitude, z, count, color, speed);
          break;
        }
        case "hull_hit":
        case "shield_hit": {
          if (event.x === undefined || event.y === undefined) break;
          const dist = Math.hypot(event.x - player.x, event.y - player.y);
          if (dist <= 3.5) break; // inbound — vignette handles it
          const color = event.type === "shield_hit" ? 0x6ec6ff : 0xffb060;
          this.burst("impact", event.x, player.altitude, event.y, this.scaleCount(7), color, 7);
          break;
        }
        case "enemy_death": {
          if (event.x === undefined || event.y === undefined) break;
          const color = event.elite ? 0xff6b4a : 0xffc878;
          this.burst(
            "kill",
            event.x,
            player.altitude + 0.5,
            event.y,
            this.scaleCount(event.elite ? 18 : 12),
            color,
            11,
          );
          break;
        }
        case "kill_burst": {
          const color = elementTint(event.element) ?? 0xffb060;
          this.burst(
            "kill",
            event.x,
            player.altitude + 0.6,
            event.y,
            this.scaleCount(16),
            color,
            14,
          );
          break;
        }
        case "ordnance_blast": {
          const style = WEAPONS[event.weaponId]?.projectileStyle ?? "singularity";
          const color = elementTint(event.element) ?? PROJECTILE_COLORS[style];
          this.burst(
            "kill",
            event.x,
            player.altitude + 0.2,
            event.y,
            this.scaleCount(20),
            color,
            Math.max(8, event.radius),
          );
          break;
        }
        case "boost":
          this.burst(
            "boost",
            player.x,
            player.altitude,
            player.y,
            this.scaleCount(10),
            0xe8b44a,
            8,
          );
          break;
        default:
          break;
      }
    }
  }

  public update(
    dt: number,
    player: { x: number; y: number; altitude: number; facing: number; vx: number; vy: number },
    juice: CombatJuiceSample,
  ): void {
    this.timeS += dt;
    if (this.reducedMotion) return;

    for (let i = 0; i < this.vfxBudget; i += 1) {
      const p = this.particles[i]!;
      const mesh = this.meshes[i]!;
      if (!p.alive) {
        mesh.visible = false;
        continue;
      }
      p.life -= dt;
      if (p.life <= 0) {
        p.alive = false;
        mesh.visible = false;
        continue;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.vy -= (p.kind === "kill" ? 6 : 2) * dt;
      const t = p.life / p.maxLife;
      mesh.visible = true;
      mesh.position.set(p.x, p.y, p.z);
      const sizeCurve =
        p.kind === "muzzle" ? 0.55 + t * 1.1 : p.kind === "kill" ? 0.4 + t * 1.15 : 0.35 + t * 0.9;
      mesh.scale.setScalar(p.size * sizeCurve);
      const mat = mesh.material as THREE.MeshBasicMaterial;
      if (p.appliedColor !== p.color) {
        mat.color.setHex(p.color);
        p.appliedColor = p.color;
      }
      mat.opacity = t * (p.kind === "muzzle" ? 0.95 : p.kind === "kill" ? 0.85 : 0.75);
    }

    this.updateSpeedLines(player, juice.boostLines);
  }

  public dispose(): void {
    this.scene.remove(this.root);
    disposeObject(this.root);
  }

  private scaleCount(desired: number): number {
    const ratio = this.vfxBudget / MAX_PARTICLES;
    return Math.max(1, Math.round(desired * ratio));
  }

  private clear(): void {
    for (const p of this.particles) p.alive = false;
    for (const mesh of this.meshes) mesh.visible = false;
    for (const line of this.speedLines) line.visible = false;
  }

  private burst(
    kind: BurstKind,
    x: number,
    y: number,
    z: number,
    count: number,
    color: number,
    speed: number,
  ): void {
    let spawned = 0;
    for (let i = 0; i < this.vfxBudget && spawned < count; i += 1) {
      const p = this.particles[i]!;
      if (p.alive) continue;
      const angle = Math.random() * Math.PI * 2;
      const elev = (Math.random() - 0.35) * 0.9;
      const spd = speed * (0.45 + Math.random() * 0.7);
      p.alive = true;
      p.kind = kind;
      p.maxLife =
        kind === "kill" ? 0.42 : kind === "muzzle" ? 0.09 : kind === "boost" ? 0.32 : 0.22;
      p.life = p.maxLife;
      p.x = x;
      p.y = y;
      p.z = z;
      p.vx = Math.cos(angle) * spd;
      p.vz = Math.sin(angle) * spd;
      p.vy = elev * spd;
      p.size =
        kind === "kill"
          ? 1.45 + Math.random() * 0.35
          : kind === "muzzle"
            ? 1.9
            : kind === "boost"
              ? 1.1 + Math.random() * 0.4
              : 0.9 + Math.random() * 0.5;
      p.color = color;
      spawned += 1;
    }
  }

  private updateSpeedLines(
    player: { x: number; y: number; altitude: number; facing: number; vx: number; vy: number },
    amount: number,
  ): void {
    const speed = Math.hypot(player.vx, player.vy);
    const show = this.speedLineBudget > 0 && amount > 0.08 && speed > 4;
    for (let i = 0; i < this.speedLines.length; i += 1) {
      const line = this.speedLines[i]!;
      if (!show || i >= this.speedLineBudget) {
        line.visible = false;
        continue;
      }
      line.visible = true;
      const seed = i * 12.9898 + this.timeS * 3.1;
      const side = Math.sin(seed) * (6 + (i % 5));
      const along = -8 - (i % 7) * 1.4 - ((this.timeS * speed * 0.15) % 6);
      const up = player.altitude + Math.cos(seed * 1.3) * 2.2;
      const fx = Math.cos(player.facing);
      const fz = Math.sin(player.facing);
      const px = -fz;
      const pz = fx;
      const bx = player.x + fx * along + px * side;
      const bz = player.y + fz * along + pz * side;
      const len = 3.4 + amount * 5.2;
      const pos = this.speedLineGeom[i]!.getAttribute("position") as THREE.BufferAttribute;
      pos.setXYZ(0, bx, up, bz);
      pos.setXYZ(1, bx - fx * len, up - 0.15, bz - fz * len);
      pos.needsUpdate = true;
      const mat = line.material as THREE.LineBasicMaterial;
      mat.opacity = 0.18 + amount * 0.52;
    }
  }
}
